import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Bounce, toast } from 'react-toastify';
import ProjectTaskDetail from './ProjectTaskDetail';
import {
  JoinShadowSessionModal,
  StartShadowSessionModal,
  StartSoloSessionModal,
} from '../Modals';
import { ICONS } from '../../constants/icons';
import { formatDate } from '../../helpers/formatDate';
import {
  getTaskDateBounds,
  getTaskDateUnavailableReason,
  isTaskDateAvailable,
} from '../../helpers/projectDateRange';
import {
  createProjectSession,
  getProjectCollaborators,
  joinProjectSession,
  resolveTaskSessionAction,
  taskMatchesOngoingSession,
  withTaskOngoingSession,
} from '../../helpers/session';
import { projectTabReturnTo } from '../../helpers/sessionReturnTo';
import { useSessionDetailPlayer } from '../FloatingPlayer';

const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MAX_VISIBLE_EVENTS = 2;
const SKELETON_CELLS = 35;
const SKELETON_AGENDA_DAYS = 4;

const skeletonChipCount = (index) => {
  if (index % 11 === 3) return 2;
  if (index % 5 === 1 || index % 7 === 4) return 1;
  return 0;
};

export const ProjectCalendarSkeleton = () => (
  <div
    className="project-calendar project-calendar--skeleton"
    aria-busy="true"
    aria-label="Loading calendar"
  >
    <div className="project-calendar__grid" role="presentation">
      {WEEKDAYS.map((day) => (
        <div key={day} className="project-calendar__day-head">
          {day}
        </div>
      ))}
      {Array.from({ length: SKELETON_CELLS }).map((_, index) => (
        <div
          key={`skeleton-cell-${index}`}
          className="project-calendar__cell project-calendar__cell--skeleton"
        >
          <div className="project-calendar__day-header">
            <span className="project-calendar__skeleton-date" />
          </div>
          <div className="project-calendar__events">
            {Array.from({ length: skeletonChipCount(index) }).map((__, chipIdx) => (
              <span
                key={`skeleton-chip-${index}-${chipIdx}`}
                className="project-calendar__skeleton-chip"
              />
            ))}
          </div>
        </div>
      ))}
    </div>

    <div className="project-calendar__agenda project-calendar__agenda--skeleton">
      {Array.from({ length: SKELETON_AGENDA_DAYS }).map((_, index) => (
        <section key={`skeleton-agenda-${index}`} className="project-calendar__agenda-day">
          <span className="project-calendar__skeleton-agenda-date" />
          <span className="project-calendar__skeleton-chip" />
          <span className="project-calendar__skeleton-chip" />
        </section>
      ))}
    </div>
  </div>
);

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const buildGridDays = (year, month) => {
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev = new Date(year, month, 0).getDate();
  const today = new Date();
  const cells = [];

  for (let i = firstDow - 1; i >= 0; i -= 1) {
    cells.push({ day: daysInPrev - i, month: 'prev' });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({
      day,
      month: 'current',
      today:
        today.getFullYear() === year &&
        today.getMonth() === month &&
        today.getDate() === day,
    });
  }

  let nextDay = 1;
  while (cells.length < 35 || cells.length % 7 !== 0) {
    cells.push({ day: nextDay, month: 'next' });
    nextDay += 1;
    if (cells.length >= 42) break;
  }

  return cells;
};

const formatAgendaDate = (year, month, day) => {
  const date = new Date(year, month, day);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

const formatDueDate = (year, month, day) =>
  new Date(year, month, day).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const formatIsoDate = (year, month, day) => {
  const monthPart = String(month + 1).padStart(2, '0');
  const dayPart = String(day).padStart(2, '0');
  return `${year}-${monthPart}-${dayPart}`;
};

const toTaskDetail = (evt, year, month) => {
  const ownerName =
    evt.assignee?.name ||
    String(evt.owner || 'Unassigned').replace(/ \+\d+$/, '');

  return {
    task: {
      id: evt.id,
      title: evt.title,
      dueDate: evt.dueDate || formatDueDate(year, month, evt.day),
      description: evt.description || '',
      collaborators: evt.collaborators || [],
      sessionActivity: evt.sessionActivity || [],
      timeTracking: evt.timeTracking || {
        logged: 0,
        total: 1,
        remaining: 1,
      },
      onGoingSessionId: evt.onGoingSessionId || evt.on_going_session_id || null,
      on_going_session_id: evt.onGoingSessionId || evt.on_going_session_id || null,
      onGoingSessionStatus:
        evt.onGoingSessionStatus || evt.on_going_session_status || null,
      on_going_session_status:
        evt.onGoingSessionStatus || evt.on_going_session_status || null,
      onGoingSessionUserStatus:
        evt.onGoingSessionUserStatus || evt.on_going_session_user_status || null,
      on_going_session_user_status:
        evt.onGoingSessionUserStatus || evt.on_going_session_user_status || null,
    },
    owner: {
      ...(evt.assignee || {
        name: ownerName,
        avatar: evt.assignee?.avatar || evt.avatar || null,
        role: evt.role || 'Member',
        roleTone: evt.isMine ? 'blue' : 'purple',
      }),
      isYou: Boolean(evt.isMine),
    },
  };
};

const markTaskSessionJoined = (task, sessionId) =>
  withTaskOngoingSession(task, {
    sessionId,
    sessionStatus: 'active',
    userStatus: 'joined',
  });

const clearTaskSessionIfMatches = (task, leftSessionId) =>
  taskMatchesOngoingSession(task, leftSessionId)
    ? withTaskOngoingSession(task)
    : task;

const CalendarDayTasksPanel = ({ dateLabel, events, onSelect, onClose }) => {
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  if (!events?.length) return null;

  return createPortal(
    <>
      <div
        className="project-calendar__day-panel-backdrop"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className="project-calendar__day-panel"
        role="dialog"
        aria-modal="true"
        aria-label={`Tasks on ${dateLabel}`}
      >
        <header className="project-calendar__day-panel-header">
          <div className="project-calendar__day-panel-copy">
            <h2 className="project-calendar__day-panel-title">{dateLabel}</h2>
            <p className="project-calendar__day-panel-count">
              {events.length} {events.length === 1 ? 'task' : 'tasks'}
            </p>
          </div>
          <button
            type="button"
            className="project-calendar__day-panel-close"
            onClick={onClose}
            aria-label="Close"
          >
            <img src={ICONS.removeCircle} alt="" width={20} height={20} />
          </button>
        </header>
        <div className="project-calendar__day-panel-list">
          {events.map((evt) => (
            <button
              key={evt.id}
              type="button"
              className={`project-calendar__event project-calendar__event--${
                evt.color || 'black'
              }`}
              onClick={() => onSelect(evt)}
            >
              <span className="project-calendar__event-title">{evt.title}</span>
              <span className="project-calendar__event-owner">{evt.owner}</span>
            </button>
          ))}
        </div>
      </aside>
    </>,
    document.body
  );
};

/**
 * ProjectCalendar
 * ------------------------------------------------------------------
 * Month grid (desktop) + agenda list (mobile) for the Calendar tab.
 * Filter / month navigation come from ProjectLayout via outlet context.
 */
const ProjectCalendar = ({
  events = [],
  onDayClick,
  onTaskDeleted,
  projectStartDate = '',
  projectEndDate = '',
}) => {
  const { projectId: routeProjectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { openSessionDetail, isOpen, sessionId, minimize } =
    useSessionDetailPlayer();
  const {
    calendarFilter = 'all',
    calendarCursor,
    project,
  } = useOutletContext() || {};
  const projectId = routeProjectId || project?.id || '';
  const calendarReturnTo = useMemo(
    () => ({
      returnTo: projectTabReturnTo(location.pathname, 'calendar', projectId),
      returnLabel: 'Redirecting to calendar...',
    }),
    [location.pathname, projectId],
  );
  const currentUser = useSelector((state) => state.auth.currentUser);
  const projectCollaborators = useMemo(
    () =>
      getProjectCollaborators(project, currentUser, {
        excludeCurrentUser: true,
      }),
    [project, currentUser]
  );
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dayMenu, setDayMenu] = useState(null);
  const [sessionModal, setSessionModal] = useState(null);
  const [activeTask, setActiveTask] = useState(null);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [isJoiningSession, setIsJoiningSession] = useState(false);

  const year = calendarCursor?.year ?? new Date().getFullYear();
  const month = calendarCursor?.month ?? new Date().getMonth();

  const taskDateBounds = useMemo(
    () => getTaskDateBounds(projectStartDate, projectEndDate),
    [projectStartDate, projectEndDate],
  );

  const scheduleRangeLabel = useMemo(() => {
    const { min, max } = taskDateBounds;
    if (!min && !max) return '';

    const minLabel = min ? formatDate(min, { format: 'MMM D, YYYY' }) : null;
    const maxLabel = max ? formatDate(max, { format: 'MMM D, YYYY' }) : null;

    if (minLabel && maxLabel) return `${minLabel} – ${maxLabel}`;
    if (minLabel) return `from ${minLabel}`;
    if (maxLabel) return `through ${maxLabel}`;
    return '';
  }, [taskDateBounds]);

  const gridDays = useMemo(() => buildGridDays(year, month), [year, month]);

  useEffect(() => {
    setDayMenu(null);
    setSelectedEvent(null);
  }, [year, month, calendarFilter]);

  const visibleEvents = useMemo(() => {
    const inMonth = events.filter(
      (e) =>
        (e.year == null || e.year === year) &&
        (e.month == null || e.month === month)
    );
    if (calendarFilter === 'my') return inMonth.filter((e) => e.isMine);
    return inMonth;
  }, [events, calendarFilter, year, month]);

  const eventsByDay = useMemo(() => {
    const map = {};
    visibleEvents.forEach((evt) => {
      if (!map[evt.day]) map[evt.day] = [];
      map[evt.day].push(evt);
    });
    return map;
  }, [visibleEvents]);

  const agendaDays = useMemo(() => {
    const days = Object.keys(eventsByDay)
      .map(Number)
      .sort((a, b) => a - b);
    return days.map((day) => ({
      day,
      label: formatAgendaDate(year, month, day),
      events: eventsByDay[day],
    }));
  }, [eventsByDay, year, month]);

  const openTaskDetail = (evt) => {
    setDayMenu(null);
    setSelectedEvent(evt);
  };

  const openDayMenu = (day, dayEvents) => {
    setSelectedEvent(null);
    setDayMenu({
      day,
      label: formatAgendaDate(year, month, day),
      events: dayEvents,
    });
  };

  const handleTaskDeleted = (taskId) => {
    setSelectedEvent(null);
    setDayMenu((prev) => {
      if (!prev) return null;
      const nextEvents = prev.events.filter(
        (evt) => String(evt.id) !== String(taskId)
      );
      if (nextEvents.length === 0) return null;
      return { ...prev, events: nextEvents };
    });
    onTaskDeleted?.(taskId);
  };

  const closeSessionModal = useCallback(() => {
    setSessionModal(null);
    setActiveTask(null);
  }, []);

  const patchSelectedEventTask = useCallback((taskId, patch) => {
    setSelectedEvent((prev) => {
      if (!prev || String(prev.id) !== String(taskId)) return prev;
      return { ...prev, ...patch };
    });
  }, []);

  const handleStartSession = useCallback(
    (task, type) => {
      if (!task) return;

      const sessionAction = resolveTaskSessionAction(task);
      const ongoingSessionId =
        task.onGoingSessionId || task.on_going_session_id || null;

      if (sessionAction === 'view' && ongoingSessionId) {
        const floatingSessionId = sessionId ? String(sessionId) : '';
        const clickedId = String(ongoingSessionId);
        if (isOpen && floatingSessionId && clickedId !== floatingSessionId) {
          minimize?.();
          navigate(`/projects/${projectId}/sessions/${clickedId}`);
          return;
        }
        setSelectedEvent(null);
        openSessionDetail({
          sessionId: ongoingSessionId,
          projectId,
          project,
          ...calendarReturnTo,
        });
        return;
      }

      setActiveTask(task);
      setSelectedEvent(null);
      if (sessionAction === 'continue') {
        setSessionModal('join');
        return;
      }
      setSessionModal(type === 'shadow' ? 'shadow' : 'solo');
    },
    [
      calendarReturnTo,
      isOpen,
      minimize,
      navigate,
      openSessionDetail,
      project,
      projectId,
      sessionId,
    ]
  );

  const createSession = useCallback(
    async ({ type, note, userIds = [], title } = {}) => {
      if (!projectId || isCreatingSession) return null;

      setIsCreatingSession(true);
      try {
        const taskId = activeTask?.id;
        const result = await createProjectSession({
          type,
          projectId,
          note,
          userIds,
          title: taskId
            ? title || activeTask?.title || activeTask?.name
            : undefined,
          sessionTarget: taskId ? 'task' : 'project',
          targetIds: taskId ? [taskId] : [projectId],
        });

        if (!result.ok) {
          if (result.reason === 'empty_note') {
            toast.error('Please add a working note before starting the session.', {
              position: 'bottom-center',
              theme: 'colored',
              transition: Bounce,
            });
          } else if (result.response) {
            toast.error(result.response?.data?.message || 'Failed to create session', {
              position: 'bottom-center',
              theme: 'colored',
              transition: Bounce,
            });
          }
          return null;
        }

        toast.success(
          type === 'solo' ? 'Solo session created.' : 'Shadow session created.',
          {
            position: 'bottom-center',
            theme: 'colored',
            transition: Bounce,
          }
        );

        const createdSessionId = result.created?.id || null;
        if (createdSessionId && taskId) {
          const patch = markTaskSessionJoined(activeTask, createdSessionId);
          patchSelectedEventTask(taskId, patch);
        }

        closeSessionModal();
        if (createdSessionId) {
          openSessionDetail({
            sessionId: createdSessionId,
            projectId,
            project,
            ...calendarReturnTo,
          });
        }
        return result.created;
      } catch (err) {
        if (err?.response || err?.code) {
          return null;
        }
        toast.error(err?.message || 'Failed to create session', {
          position: 'bottom-center',
          theme: 'colored',
          transition: Bounce,
        });
        return null;
      } finally {
        setIsCreatingSession(false);
      }
    },
    [
      activeTask,
      calendarReturnTo,
      closeSessionModal,
      isCreatingSession,
      openSessionDetail,
      patchSelectedEventTask,
      project,
      projectId,
    ]
  );

  const joinSession = useCallback(
    async ({ note } = {}) => {
      if (isJoiningSession) return null;

      const ongoingSessionId =
        activeTask?.onGoingSessionId || activeTask?.on_going_session_id;
      if (!ongoingSessionId) {
        toast.error('No ongoing session found for this task.', {
          position: 'bottom-center',
          theme: 'colored',
          transition: Bounce,
        });
        return null;
      }

      setIsJoiningSession(true);
      try {
        const result = await joinProjectSession({
          sessionId: ongoingSessionId,
          note,
        });

        if (!result.ok) {
          if (result.reason === 'empty_note') {
            toast.error('Please add a working note before joining the session.', {
              position: 'bottom-center',
              theme: 'colored',
              transition: Bounce,
            });
          } else if (result.response) {
            toast.error(result.response?.data?.message || 'Failed to join session', {
              position: 'bottom-center',
              theme: 'colored',
              transition: Bounce,
            });
          }
          return null;
        }

        toast.success('Joined session.', {
          position: 'bottom-center',
          theme: 'colored',
          transition: Bounce,
        });

        const joinedTaskId = activeTask?.id;
        if (joinedTaskId) {
          patchSelectedEventTask(
            joinedTaskId,
            markTaskSessionJoined(activeTask, ongoingSessionId)
          );
        }

        closeSessionModal();
        openSessionDetail({
          sessionId: ongoingSessionId,
          projectId,
          project,
          ...calendarReturnTo,
        });
        return result.joined;
      } catch (err) {
        if (err?.response || err?.code) {
          return null;
        }
        toast.error(err?.message || 'Failed to join session', {
          position: 'bottom-center',
          theme: 'colored',
          transition: Bounce,
        });
        return null;
      } finally {
        setIsJoiningSession(false);
      }
    },
    [
      activeTask,
      calendarReturnTo,
      closeSessionModal,
      isJoiningSession,
      openSessionDetail,
      patchSelectedEventTask,
      project,
      projectId,
    ]
  );

  useEffect(() => {
    const handleSessionLeft = (event) => {
      const leftSessionId = event?.detail?.sessionId;
      if (!leftSessionId) return;

      setSelectedEvent((prev) =>
        prev ? clearTaskSessionIfMatches(prev, leftSessionId) : prev
      );
      setActiveTask((prev) =>
        prev ? clearTaskSessionIfMatches(prev, leftSessionId) : prev
      );
    };

    window.addEventListener('session-left', handleSessionLeft);
    return () => window.removeEventListener('session-left', handleSessionLeft);
  }, []);

  const selectedDetail = selectedEvent
    ? toTaskDetail(selectedEvent, year, month)
    : null;

  const renderEvent = (evt) => (
    <button
      key={evt.id}
      type="button"
      className={`project-calendar__event project-calendar__event--${evt.color || 'black'}`}
      onClick={(event) => {
        event.stopPropagation();
        openTaskDetail(evt);
      }}
    >
      <span className="project-calendar__event-title">{evt.title}</span>
      <span className="project-calendar__event-owner">{evt.owner}</span>
    </button>
  );

  return (
    <div className="project-calendar">
      {scheduleRangeLabel ? (
        <div className="project-calendar__legend" aria-label="Calendar scheduling rules">
          <p className="project-calendar__legend-text">
            Tasks can be scheduled{' '}
            <span className="project-calendar__legend-range">{scheduleRangeLabel}</span>.
            Unavailable dates are shown with a muted pattern.
          </p>
          <div className="project-calendar__legend-items">
            <span className="project-calendar__legend-item">
              <span
                className="project-calendar__legend-swatch project-calendar__legend-swatch--available"
                aria-hidden="true"
              />
              Available
            </span>
            <span className="project-calendar__legend-item">
              <span
                className="project-calendar__legend-swatch project-calendar__legend-swatch--unavailable"
                aria-hidden="true"
              />
              Unavailable
            </span>
          </div>
        </div>
      ) : null}

      <div className="project-calendar__grid" role="grid" aria-label={`${MONTH_NAMES[month]} ${year}`}>
        {WEEKDAYS.map((d) => (
          <div key={d} className="project-calendar__day-head" role="columnheader">
            {d}
          </div>
        ))}

        {gridDays.map((cell, i) => {
          const cellEvents =
            cell.month === 'current' ? eventsByDay[cell.day] || [] : [];
          const shownEvents = cellEvents.slice(0, MAX_VISIBLE_EVENTS);
          const hiddenCount = cellEvents.length - shownEvents.length;
          const isoDate =
            cell.month === 'current'
              ? formatIsoDate(year, month, cell.day)
              : '';
          const isAvailable =
            cell.month === 'current' &&
            isTaskDateAvailable(isoDate, projectStartDate, projectEndDate);
          const unavailableReason =
            cell.month === 'current' && !isAvailable
              ? getTaskDateUnavailableReason(
                  isoDate,
                  projectStartDate,
                  projectEndDate,
                )
              : null;
          const canCreateOnDay =
            Boolean(onDayClick) && cell.month === 'current' && isAvailable;
          return (
            <div
              key={`${cell.month}-${cell.day}-${i}`}
              className={`project-calendar__cell project-calendar__cell--${cell.month}${
                cell.today ? ' project-calendar__cell--today' : ''
              }${canCreateOnDay ? ' project-calendar__cell--clickable' : ''}${
                unavailableReason ? ' project-calendar__cell--unavailable' : ''
              }`}
              role="gridcell"
              title={unavailableReason || undefined}
              onClick={
                canCreateOnDay
                  ? () => onDayClick(isoDate)
                  : undefined
              }
            >
              <div className="project-calendar__day-header">
                <button
                  type="button"
                  className={`project-calendar__date${
                    cell.today ? ' project-calendar__date--today' : ''
                  }${unavailableReason ? ' project-calendar__date--unavailable' : ''}`}
                  onClick={(event) => {
                    if (canCreateOnDay) return;
                    event.stopPropagation();
                    if (cellEvents.length > 0) openDayMenu(cell.day, cellEvents);
                  }}
                  disabled={!canCreateOnDay && cellEvents.length === 0}
                  aria-label={
                    canCreateOnDay
                      ? `Create task on ${formatAgendaDate(year, month, cell.day)}`
                      : unavailableReason
                        ? `${formatAgendaDate(year, month, cell.day)}: ${unavailableReason}`
                        : cellEvents.length > 0
                          ? `View ${cellEvents.length} tasks on ${formatAgendaDate(
                              year,
                              month,
                              cell.day,
                            )}`
                          : undefined
                  }
                >
                  {cell.day}
                </button>
              </div>
              <div className="project-calendar__events">
                {shownEvents.map(renderEvent)}
              </div>
              {hiddenCount > 0 ? (
                <button
                  type="button"
                  className="project-calendar__more"
                  onClick={(event) => {
                    event.stopPropagation();
                    openDayMenu(cell.day, cellEvents);
                  }}
                >
                  +{hiddenCount} more
                </button>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="project-calendar__agenda" aria-label="Agenda">
        {agendaDays.length === 0 ? (
          <p className="project-calendar__agenda-empty">
            No tasks scheduled this month.
          </p>
        ) : (
          agendaDays.map(({ day, label, events: dayEvents }) => (
            <section key={day} className="project-calendar__agenda-day">
              <h3 className="project-calendar__agenda-date">{label}</h3>
              <div className="project-calendar__agenda-events">
                {dayEvents.map(renderEvent)}
              </div>
            </section>
          ))
        )}
      </div>

      {dayMenu ? (
        <CalendarDayTasksPanel
          dateLabel={dayMenu.label}
          events={dayMenu.events}
          onSelect={openTaskDetail}
          onClose={() => setDayMenu(null)}
        />
      ) : null}

      <ProjectTaskDetail
        task={selectedDetail?.task}
        owner={selectedDetail?.owner}
        onClose={() => setSelectedEvent(null)}
        onStartSession={
          selectedDetail?.owner?.isYou
            ? (type) => handleStartSession(selectedDetail.task, type)
            : undefined
        }
        startSessionDisabled={Boolean(
          selectedDetail && !selectedDetail.owner?.isYou
        )}
        onTaskDeleted={handleTaskDeleted}
      />

      <StartSoloSessionModal
        isOpen={sessionModal === 'solo'}
        onClose={closeSessionModal}
        project={project}
        task={activeTask}
        onStart={({ note, title }) =>
          createSession({ type: 'solo', note, title, userIds: [] })
        }
        isSubmitting={isCreatingSession}
      />
      <StartShadowSessionModal
        isOpen={sessionModal === 'shadow'}
        onClose={closeSessionModal}
        project={project}
        task={activeTask}
        collaborators={projectCollaborators}
        onStart={({ note, userIds, title }) =>
          createSession({ type: 'shadow', note, userIds, title })
        }
        isSubmitting={isCreatingSession}
      />
      <JoinShadowSessionModal
        isOpen={sessionModal === 'join'}
        onClose={closeSessionModal}
        project={project}
        collaborators={activeTask?.collaborators || []}
        onJoin={joinSession}
        isSubmitting={isJoiningSession}
      />
    </div>
  );
};

export default ProjectCalendar;
